import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import "./index.css";
/* Body / UI workhorse ; geometric space-age sans, readable at text sizes */
import "@fontsource/exo-2/latin-400.css";
import "@fontsource/exo-2/latin-500.css";
import "@fontsource/exo-2/latin-600.css";
import "@fontsource/exo-2/latin-700.css";
import App from "./App";
// import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import store from "./store/store";
import axios from "axios";
import { ErrorBoundary } from "./HOC/ErrorHandling/ErrorBoundary/ErrorBoundary";
import { initUtmTracking } from "./data/utmTracking";

////////////////////////
///    Axios Config
///////////////////////
const inDevelopment = process.env.NODE_ENV === "development";
// Production API (deployed). Local schema changes only apply when the
// frontend points at localhost ; otherwise saves hit production and strip
// fields the live schema does not know about (e.g. preReleaseMessage).
const PRODUCTION_API = "https://api.ignite-elysium.com/";
const LOCAL_API = "http://localhost:8000";

/**
 * Dev API host: on the Mac, localhost is fine. On a phone/tablet opening the
 * CRA app via the Mac's LAN IP (e.g. http://192.168.1.108:3000), "localhost"
 * would mean the phone itself ; so reuse the page hostname on port 8000.
 * Override anytime: REACT_APP_API_URL=http://192.168.1.108:8000
 * ROLLBACK: always use LOCAL_API only (delete getDevApiBase).
 */
function getDevApiBase() {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv).trim();

  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host && host !== "localhost" && host !== "127.0.0.1") {
      return `http://${host}:8000`;
    }
  }
  return LOCAL_API;
}

axios.defaults.baseURL = inDevelopment ? getDevApiBase() : PRODUCTION_API;

initUtmTracking();

// Force production API while developing (comment out LOCAL_API line above):
// if (inDevelopment) axios.defaults.baseURL = PRODUCTION_API;

console.log(
  "%cAxios Default Base URL:",
  "color:#fff;background:#287094;padding:5px;border-radius:0 25px 25px 0",
  axios.defaults.baseURL,
);

if (inDevelopment) {
  console.log(
    "%cRunning in DEV MODE with the base URL:",
    "color:#fff;background:#287094;padding:5px;border-radius:0 25px 25px 0",
    axios.defaults.baseURL,
  );
  const base = String(axios.defaults.baseURL || "");
  const isLocalBackend =
    base.includes("localhost") ||
    base.includes("127.0.0.1") ||
    /^https?:\/\/(10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/.test(base);
  if (isLocalBackend) {
    console.log(
      "%cUsing LOCAL backend — restart MS1-BACKEND after schema changes.",
      "color:#fff;background:#007215;padding:5px;border-radius:0 25px 25px 0",
    );
  } else {
    console.warn(
      "Dev mode is pointing at PRODUCTION API. Local backend schema changes will not apply to saves.",
    );
  }
}

axios.defaults.headers.common["Authorization"] = "AUTH TOKEN";
axios.defaults.headers.post["Content-Type"] = "application/json";

axios.interceptors.request.use(
  (request) => {
    // alert("In REQ");
    if (inDevelopment)
      console.log(
        "%cRequest:",
        "color:#94283a;background:#f0f0ef;padding:5px;border-radius:0 25px 25px 0",
        request,
      );
    // Edit request config
    return request;
  },
  (error) => {
    // alert("In REQ Error");
    if (inDevelopment)
      console.log(
        "%cERROR:",
        "color:#f0f0ef;background:#ff0000;padding:32px;border-radius:0 25px 25px 0",
        error,
      );
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (response) => {
    // alert("In Res");
    if (inDevelopment)
      console.log(
        "%cResponse:",
        "color:#f0f0ef;background:#94283a;padding:5px;border-radius:0 25px 25px 0",
        response,
      );

    return response;
  },

  (error) => {
    if (inDevelopment)
      console.log(
        "%cERROR:",
        "color:#f0f0ef;background:#ff0000;padding:32px;border-radius:0 25px 25px 0",
        error,
      );
    return Promise.reject(error);
  },
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Router basename={process.env.PUBLIC_URL}>
    <React.StrictMode>
      <Provider store={store}>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </Provider>
    </React.StrictMode>
  </Router>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
